import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import CustomButton from './CustomButton';
import { useRegister } from '../context/RegisterContext';
import { LoginData } from '../types/RegisterType';
import './StepLogin.css';

interface Props {
  onNext: () => void;
  onBack: () => void;
}

const schema = yup.object({
  email: yup.string().required('Campo obrigatório').email('E-mail inválido'),
  password: yup.string().required('Campo obrigatório').min(8, 'Mínimo de 8 caracteres'),
  confirmPassword: yup
    .string()
    .required('Campo obrigatório')
    .oneOf([yup.ref('password')], 'As senhas não coincidem'),
});

const EyeOpenIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeClosedIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

const StepLogin: React.FC<Props> = ({ onNext, onBack }) => {
  const { formData, updateLogin } = useRegister();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginData>({
    resolver: yupResolver(schema),
    defaultValues: {
      email: formData.login.email || formData.responsavel.email,
      password: formData.login.password,
      confirmPassword: formData.login.confirmPassword,
    },
  });

  const onSubmit = (data: LoginData) => {
    updateLogin(data);
    onNext();
  };

  return (
    <form className="step-login" onSubmit={handleSubmit(onSubmit)}>
      <h2 className="step-login-title">Login</h2>
      <p className="step-login-subtitle">Criar acesso ao painel do parceiro.</p>

      <div className="form-row">
        <div className="form-group form-group-full">
          <label htmlFor="email" className="form-label">E-mail para acesso</label>
          <input
            id="email"
            type="email"
            className={`form-input ${errors.email ? 'error' : ''}`}
            placeholder="email@exemplo.com"
            {...register('email')}
          />
          {errors.email && <span className="error-message">{errors.email.message}</span>}
        </div>
      </div>

      <div className="form-row form-row-two">
        <div className="form-group">
          <label htmlFor="password" className="form-label">
            Crie a senha <span className="label-hint">(mínimo 8 caracteres)</span>
          </label>
          <div className="password-input-wrapper">
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              className={`form-input ${errors.password ? 'error' : ''}`}
              placeholder="********"
              {...register('password')}
            />
            <button
              type="button"
              className="password-toggle-btn"
              onClick={() => setShowPassword(v => !v)}
              tabIndex={-1}
            >
              {showPassword ? <EyeClosedIcon /> : <EyeOpenIcon />}
            </button>
          </div>
          {errors.password && <span className="error-message">{errors.password.message}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="confirmPassword" className="form-label">
            Confirme a senha <span className="label-hint">(mínimo 8 caracteres)</span>
          </label>
          <div className="password-input-wrapper">
            <input
              id="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              className={`form-input ${errors.confirmPassword ? 'error' : ''}`}
              placeholder="********"
              {...register('confirmPassword')}
            />
            <button
              type="button"
              className="password-toggle-btn"
              onClick={() => setShowConfirmPassword(v => !v)}
              tabIndex={-1}
            >
              {showConfirmPassword ? <EyeClosedIcon /> : <EyeOpenIcon />}
            </button>
          </div>
          {errors.confirmPassword && <span className="error-message">{errors.confirmPassword.message}</span>}
        </div>
      </div>

      <div className="form-actions form-actions-two">
        <CustomButton
          type="button"
          variant="outlined"
          onClick={onBack}
          sx={{
            borderColor: '#FF914D',
            color: '#FF914D',
            width: '150px',
            '&:hover': {
              borderColor: '#FF914D',
              backgroundColor: 'rgba(255, 145, 77, 0.04)',
              color: '#FF914D',
            },
          }}
        >
          Voltar
        </CustomButton>
        <CustomButton type="submit" variant="contained" customBackground="#46558D" sx={{ width: '150px' }}>
          Continuar
        </CustomButton>
      </div>
    </form>
  );
};

export default StepLogin;
